import type { ProductDefinition } from './types.js';

/**
 * Will this photo print well?
 *
 * Asked in the shopper's browser, before the upload starts, so the answer is
 * instant and the shopper can still act on it by choosing a different picture.
 *
 * It is advice, never a gate. The shop's rule (2026-09-16) is that a shopper
 * is always allowed to upload and order whatever they have: a small phone
 * picture on a keychain is their call, and a refused upload is a lost sale
 * with nothing to show for it. So the verdict is `ok` or `warn` — the warning
 * says how the print will look and what would be better, and the upload goes
 * ahead either way. The merchant sees the same measurement on the order.
 *
 * The requirement comes from the template, not from a table of product types.
 * A print scene is authored at a real size and a real DPI, so the element's own
 * rectangle already *is* the pixel count that prints 1:1 — no second place to
 * configure, nothing to keep in step.
 *
 * The owner's rule (2026-09-20): shoppers are never told a photo is "bad",
 * "blurry" or low-resolution, and never shown pixel counts or DPI numbers.
 * Every warn case reads the same one calm, numberless sentence — see
 * `PHOTO_TEXTS`. The measurement (`ppi`, `severe`, `sharpness`) stays exact;
 * only the words a shopper reads are softened.
 */

/** The only two sentences a shopper ever sees about their photo. */
export const PHOTO_TEXTS = {
  ok: 'Slika je u redu.',
  warn: 'Slika je u redu i može se koristiti. Ako imaš veću ili oštriju verziju, otisak će biti još lepši.',
};

export interface PhotoRequirement {
  /** Pixels needed to print this slot at the scene's own resolution. */
  w: number;
  h: number;
  dpi: number;
}

export type PhotoVerdict = 'ok' | 'warn';

export interface PhotoAssessment {
  verdict: PhotoVerdict;
  /** Effective resolution of this photo at the printed size. */
  ppi: number;
  /**
   * The print will be visibly soft (under `FLOOR_PPI`), as opposed to merely
   * softer than ideal. Wording and colour differ; nothing else does.
   */
  severe?: boolean;
  /** Variance of the Laplacian; higher is sharper. Undefined if not measured. */
  sharpness?: number;
  reasons: string[];
}

/**
 * Below this the print is visibly soft at arm's length on any product. The
 * line only decides how strongly the shopper is told — see the module note.
 */
export const FLOOR_PPI = 100;

/**
 * Sharpness below which a photo is called soft.
 *
 * Deliberately conservative: a false "blurry" annoys, a missed one costs
 * nothing but a slightly soft keychain. This wants calibrating against real
 * returns before it is tightened.
 */
export const SOFT_VARIANCE = 45;

/** Long edge the image is reduced to before measuring, so the score is comparable. */
const MEASURE_EDGE = 512;

/**
 * What this question's photo slot needs, in pixels.
 *
 * When one question drives several slots — a layout swatch filling four frames —
 * the largest wins, because that is the one that will show the softness.
 */
export function requirementFor(
  product: ProductDefinition,
  optionId: string,
): PhotoRequirement | undefined {
  const elementIds = new Set(
    product.bindings.filter((b) => b.optionId === optionId).map((b) => b.elementId),
  );
  if (elementIds.size === 0) return undefined;

  let best: PhotoRequirement | undefined;
  for (const side of product.sides) {
    // Preview scenes are mockups at screen size; only the print side says what
    // the press actually needs.
    if (side.target === 'preview') continue;
    for (const element of side.elements) {
      if (!elementIds.has(element.id) || element.type !== 'image') continue;
      const w = Math.round(Math.abs(element.transform.w));
      const h = Math.round(Math.abs(element.transform.h));
      if (w <= 0 || h <= 0) continue;
      if (!best || w * h > best.w * best.h) best = { w, h, dpi: side.dpi || 300 };
    }
  }
  return best;
}

/**
 * Judges a photo's pixel count against what the slot needs.
 *
 * Two strengths of the same advice: under the authored size the print is
 * softer than designed; under the floor it is visibly soft. Both say what
 * size would print crisply and both let the shopper carry on.
 */
export function assessResolution(
  photo: { w: number; h: number },
  requirement: PhotoRequirement,
): PhotoAssessment {
  // The slot is filled, so the limiting dimension is whichever the photo has
  // proportionally less of.
  const ratio = Math.min(photo.w / requirement.w, photo.h / requirement.h);
  const exactPpi = ratio * requirement.dpi;
  // Compared before rounding: at 99.9 PPI a rounded value is exactly 100 and
  // slips past the floor, which is the one number here that must not be soft.
  const ppi = Math.round(exactPpi);
  if (exactPpi < FLOOR_PPI) {
    return {
      verdict: 'warn',
      severe: true,
      ppi,
      reasons: [PHOTO_TEXTS.warn],
    };
  }
  if (ratio < 1) {
    return {
      verdict: 'warn',
      ppi,
      reasons: [PHOTO_TEXTS.warn],
    };
  }
  return { verdict: 'ok', ppi, reasons: [] };
}

/**
 * How sharply a placed Mini Me head will print, in pixels per inch.
 *
 * The photo-level check compares the whole photo with the slot, but on a Mini
 * Me product only the HEAD is enlarged into the slot: a head that is a small
 * part of a big photo passes that check and still prints soft. This measures
 * the head's own pixels (hair top to chin, in the cut-out) against the height
 * it is drawn at on the print side.
 */
export function headPrintPpi(
  headSourcePx: number,
  drawnScenePx: number,
  dpi: number,
): number {
  if (!(headSourcePx > 0) || !(drawnScenePx > 0) || !(dpi > 0)) return Infinity;
  return (headSourcePx / drawnScenePx) * dpi;
}

/**
 * Variance of the Laplacian, measured on a fixed-size greyscale copy.
 *
 * The fixed size is essential and is what naive implementations get wrong: the
 * raw score scales with resolution, so a threshold tuned on a 640px photo calls
 * every 4000px phone photo blurry. Reducing to a constant long edge first makes
 * one threshold mean the same thing for every upload.
 */
export function sharpnessOf(pixels: ImageData): number {
  const { data, width, height } = pixels;
  const grey = new Float32Array(width * height);
  for (let i = 0, p = 0; i < data.length; i += 4, p++) {
    // Rec. 601 luma — matching what the eye weights, not a flat average.
    grey[p] = 0.299 * data[i]! + 0.587 * data[i + 1]! + 0.114 * data[i + 2]!;
  }

  let sum = 0;
  let sumSquares = 0;
  let count = 0;
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const i = y * width + x;
      const laplacian =
        4 * grey[i]! - grey[i - 1]! - grey[i + 1]! - grey[i - width]! - grey[i + width]!;
      sum += laplacian;
      sumSquares += laplacian * laplacian;
      count++;
    }
  }
  if (count === 0) return 0;
  const mean = sum / count;
  return sumSquares / count - mean * mean;
}

/** Reduces an image to the measuring size and returns its pixels. */
export function measurementPixels(
  source: CanvasImageSource & { width: number; height: number },
): ImageData | undefined {
  const scale = Math.min(1, MEASURE_EDGE / Math.max(source.width, source.height));
  const w = Math.max(8, Math.round(source.width * scale));
  const h = Math.max(8, Math.round(source.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return undefined;
  context.drawImage(source, 0, 0, w, h);
  try {
    return context.getImageData(0, 0, w, h);
  } catch {
    // A cross-origin source taints the canvas. Not worth failing an upload over.
    return undefined;
  }
}

/** Combines both checks. Resolution sets the strength; sharpness can only add a warning. */
export function assessPhoto(
  photo: { w: number; h: number },
  requirement: PhotoRequirement,
  sharpness?: number,
): PhotoAssessment {
  const resolution = assessResolution(photo, requirement);
  if (sharpness === undefined || sharpness >= SOFT_VARIANCE) {
    return { ...resolution, sharpness };
  }
  return {
    ...resolution,
    verdict: 'warn',
    sharpness,
    // Same one calm sentence, whether it's the resolution or the sharpness
    // check that tripped the warning — never both stacked.
    reasons: [PHOTO_TEXTS.warn],
  };
}
